import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { api, ApiError, errorText, newRequestId, type InputAcceptedDto, type InputStatusDto, type PlanDto, type PlanOperationDto } from "../api/client";
import { PersonChips } from "../components/EventList";
import { PlanDecision } from "../components/PlanDecision";
import { Icon } from "../components/Icon";
import { Questions } from "../components/Questions";
import { formatQuantity, formatShortDate, formatTimeRange } from "../format";
import { useSession } from "../session";
import { pickAudioMime } from "./Recorder";
import { VoiceConversation } from "./VoiceConversation";
import { uploadAsset } from "./uploads";

const MAX_SECONDS = 120;
const WORKING = ["waiting_for_assets", "queued", "transcribing", "interpreting"];

const VoiceContext = createContext<{ open: () => void; converse: (area?: string) => void } | null>(null);

/**
 * Ein globaler Sprach-Assistent: Mikrofon-Knopf (Diktat) und Gespräch (Realtime, ADR-0014). Das Gespräch startet die
 * Android-Hülle nach „Hey Jarvis“ über window.fpVoice.start(area); im Browser per langem Druck auf das Mikrofon.
 */
export function VoiceProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<number | null>(null);
  const [talk, setTalk] = useState<{ key: number; area?: string } | null>(null);
  const open = useCallback(() => setSession(Date.now()), []);
  const converse = useCallback((area?: string) => setTalk((t) => t ?? { key: Date.now(), area }), []);
  useEffect(() => {
    window.fpVoice = { start: (area?: string) => converse(area) };
    return () => {
      delete window.fpVoice;
    };
  }, [converse]);
  return (
    <VoiceContext.Provider value={{ open, converse }}>
      {children}
      {session !== null && <VoiceSheet key={session} onClose={() => setSession(null)} />}
      {talk && <VoiceConversation key={talk.key} area={talk.area} onClose={() => setTalk(null)} />}
    </VoiceContext.Provider>
  );
}

export function useVoice() {
  return useContext(VoiceContext) ?? { open: () => undefined, converse: () => undefined };
}

/** Mikrofon-Knopf: ein Tipp startet die Aufnahme. */
export function VoiceButton({ className = "", children }: { className?: string; children?: ReactNode }) {
  const voice = useVoice();
  // Kurz tippen = Diktat; lange drücken (0,6 s) = Gespräch mit dem Planer.
  const timer = useRef<number | undefined>(undefined);
  const longPressed = useRef(false);
  const down = () => {
    longPressed.current = false;
    timer.current = window.setTimeout(() => {
      longPressed.current = true;
      voice.converse();
    }, 600);
  };
  const up = () => window.clearTimeout(timer.current);
  return (
    <button type="button" className={`voice-button ${className}`} aria-label="Sprechen" title="Tippen: diktieren · Lange drücken: Gespräch"
      onPointerDown={down} onPointerUp={up} onPointerLeave={up} onContextMenu={(e) => e.preventDefault()}
      onClick={() => !longPressed.current && voice.open()}>
      {children ?? <Icon name="mic" />}
    </button>
  );
}

type Phase = "recording" | "uploading" | "processing" | "error";

function VoiceSheet({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [phase, setPhase] = useState<Phase>("recording");
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [inputId, setInputId] = useState<string | null>(null);
  const recorder = useRef<MediaRecorder | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const chunks = useRef<Blob[]>([]);
  const send = useRef(true);
  const mime = pickAudioMime();

  const release = () => {
    stream.current?.getTracks().forEach((t) => t.stop());
    stream.current = null;
  };

  const submit = async (blob: Blob) => {
    setPhase("uploading");
    try {
      const asset = await uploadAsset(blob, `sprache.${blob.type.split("/")[1] ?? "webm"}`, () => undefined);
      if (asset.status !== "available") throw new Error("Die Aufnahme wurde vom Server abgelehnt.");
      const accepted = await api<InputAcceptedDto>("/api/v1/inputs", {
        body: {
          schemaVersion: "1.0",
          clientRequestId: newRequestId("voice"),
          text: null,
          assetIds: [asset.id],
          clientCreatedAt: new Date().toISOString(),
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          locale: navigator.language,
          skipTranscriptConfirmation: true,
        },
      });
      setInputId(accepted.inputId);
      setPhase("processing");
    } catch (e) {
      setError(e instanceof ApiError ? errorText(e) : e instanceof Error ? e.message : "Übertragung fehlgeschlagen.");
      setPhase("error");
    }
  };

  // Aufnahme startet sofort beim Öffnen (der Tipp auf das Mikrofon ist die bewusste Handlung).
  useEffect(() => {
    let timer: number | undefined;
    (async () => {
      if (!mime || !navigator.mediaDevices?.getUserMedia) {
        setError("Sprachaufnahme wird von diesem Browser nicht unterstützt.");
        setPhase("error");
        return;
      }
      try {
        stream.current = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch {
        setError("Kein Zugriff auf das Mikrofon. Bitte in den Browser-Einstellungen erlauben.");
        setPhase("error");
        return;
      }
      chunks.current = [];
      const rec = new MediaRecorder(stream.current, { mimeType: mime });
      rec.ondataavailable = (e) => e.data.size > 0 && chunks.current.push(e.data);
      rec.onstop = () => {
        release();
        window.clearInterval(timer);
        if (send.current && chunks.current.length > 0) void submit(new Blob(chunks.current, { type: mime.split(";")[0] }));
      };
      recorder.current = rec;
      rec.start(500);
      timer = window.setInterval(() => setSeconds((s) => {
        if (s + 1 >= MAX_SECONDS) recorder.current?.state === "recording" && recorder.current.stop();
        return s + 1;
      }), 1000);
    })();
    return () => {
      send.current = false;
      if (recorder.current?.state === "recording") recorder.current.stop();
      release();
      window.clearInterval(timer);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const finish = () => {
    send.current = true;
    if (recorder.current?.state === "recording") recorder.current.stop();
  };
  const cancel = () => {
    send.current = false;
    if (recorder.current?.state === "recording") recorder.current.stop();
    onClose();
  };

  const status = useQuery({
    queryKey: ["inputs", inputId],
    queryFn: () => api<InputStatusDto>(`/api/v1/inputs/${inputId}`),
    enabled: inputId !== null,
    refetchInterval: (q) => (!q.state.data || WORKING.includes(q.state.data.status) ? 1200 : false),
  });
  const s = status.data;
  const plan = useQuery({
    queryKey: ["plan", s?.planId],
    queryFn: () => api<PlanDto>(`/api/v1/action-plans/${s!.planId}`),
    enabled: Boolean(s?.planId) && !WORKING.includes(s?.status ?? ""),
  });

  const done = async () => {
    await qc.invalidateQueries();
    onClose();
  };

  // Gespeichert → kurz zeigen, dann schließen.
  useEffect(() => {
    if (s?.status !== "committed") return;
    void qc.invalidateQueries();
    const t = window.setTimeout(onClose, 4000);
    return () => window.clearTimeout(t);
  }, [s?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && (phase === "recording" ? cancel() : onClose());
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  return (
    <div className="dialog-backdrop voice-backdrop" role="dialog" aria-modal="true" aria-label="Spracheingabe">
      <div className="dialog card voice-sheet">
        {phase === "recording" && (
          <>
            <button type="button" className="voice-orb recording" onClick={finish} aria-label="Aufnahme beenden und senden">
              <span aria-hidden="true">🎤</span>
            </button>
            <p className="voice-timer" role="status">Ich höre zu … {Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, "0")}</p>
            <p className="hint small">z. B. „Morgen um 15 Uhr Zahnarzt für Greta“, „Milch und zwei Packungen Nudeln“ oder „Klara hat am Freitag Training“.</p>
            <div className="row voice-actions">
              <button type="button" className="btn" onClick={cancel}>Abbrechen</button>
              <button type="button" className="btn primary grow" onClick={finish}>Fertig</button>
            </div>
          </>
        )}

        {phase === "uploading" && <Working text="Wird übertragen …" />}

        {phase === "processing" && (!s || WORKING.includes(s.status)) && (
          <Working text={s?.status === "interpreting" ? "Wird verstanden …" : s?.status === "transcribing" || s?.status === "queued" ? "Wird verschriftet …" : "Einen Moment …"} transcript={s?.transcript} />
        )}

        {phase === "processing" && s && !WORKING.includes(s.status) && (
          <Result status={s} plan={plan.data} onClose={onClose} onDone={done} />
        )}

        {phase === "error" && (
          <>
            <p className="error">{error}</p>
            <div className="row voice-actions">
              <button type="button" className="btn grow" onClick={onClose}>Schließen</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Working({ text, transcript }: { text: string; transcript?: string | null }) {
  return (
    <div className="voice-working" role="status" aria-live="polite">
      <span className="spinner" aria-hidden="true" />
      <p><strong>{text}</strong></p>
      {transcript && <blockquote>„{transcript}“</blockquote>}
    </div>
  );
}

function Result({ status, plan, onClose, onDone }: { status: InputStatusDto; plan?: PlanDto; onClose: () => void; onDone: () => void }) {
  const navigate = useNavigate();
  const ops = (plan?.operations ?? []).filter((o) => o.selected);

  return (
    <div className="voice-result">
      {status.transcript && <blockquote>„{status.transcript}“</blockquote>}

      {status.status === "committed" && (
        <>
          <p className="voice-ok">✓ Gespeichert</p>
          <OperationList ops={ops} />
          <div className="row voice-actions"><button type="button" className="btn primary grow" onClick={onClose}>Fertig</button></div>
        </>
      )}

      {["awaiting_review", "needs_answer"].includes(status.status) && plan && <PlanDecision planId={plan.id} onDone={onDone} />}

      {status.status === "needs_answer" && !plan && (
        <>
          <OperationList ops={ops} />
          <Questions inputId={status.inputId} questions={status.questions} />
          <div className="row voice-actions"><button type="button" className="btn grow" onClick={onClose}>Später im Eingang</button></div>
        </>
      )}

      {status.status === "no_action" && (
        <>
          <p>Darin habe ich keinen Termin und keinen Einkauf gefunden.</p>
          <div className="row voice-actions"><button type="button" className="btn grow" onClick={onClose}>Schließen</button></div>
        </>
      )}

      {["failed", "ai_unavailable", "rejected", "cancelled", "awaiting_transcript_confirmation"].includes(status.status) && (
        <>
          <p className="error">{status.statusMessage ?? "Das hat nicht geklappt."}</p>
          <div className="row voice-actions">
            <button type="button" className="btn" onClick={() => { onClose(); navigate(`/inbox#${status.inputId}`); }}>Im Eingang ansehen</button>
            <button type="button" className="btn grow" onClick={onClose}>Schließen</button>
          </div>
        </>
      )}
    </div>
  );
}

function OperationList({ ops }: { ops: PlanOperationDto[] }) {
  if (ops.length === 0) return null;
  return (
    <ul className="voice-ops">
      {ops.map((op) => <OperationLine key={op.index} op={op} />)}
    </ul>
  );
}

function OperationLine({ op }: { op: PlanOperationDto }) {
  const { me } = useSession();
  const p = op.payload as Record<string, unknown>;
  const persons = ((p.participants as { personId: string }[] | null) ?? []).map((x) => x.personId);
  const issues = op.issues.filter((i) => i.severity !== "info");
  let icon = "•";
  let text: ReactNode = op.type;
  switch (op.type) {
    case "calendar.create_event":
      icon = "📅";
      text = <><strong>{String(p.title)}</strong> · {formatShortDate(String(p.start))} · {formatTimeRange(String(p.start), p.end as string | null, Boolean(p.allDay))}{p.recurrence ? " · wiederholt" : ""}</>;
      break;
    case "calendar.update_event":
      icon = "✏️";
      text = <>Termin ändern{p.title ? <>: <strong>{String(p.title)}</strong></> : null}{p.start ? <> · {formatShortDate(String(p.start))}</> : null}</>;
      break;
    case "calendar.cancel_event":
    case "calendar.cancel_occurrence":
      icon = "🗑️";
      text = "Termin absagen";
      break;
    case "shopping.add_item":
      icon = "🛒";
      text = <><strong>{String(p.name)}</strong> {formatQuantity(p.quantity as number | null, p.unit as string | null)}</>;
      break;
    case "shopping.set_item_status":
      icon = "☑️";
      text = "Einkaufsartikel abhaken";
      break;
    case "shopping.update_item":
      icon = "✏️";
      text = <>Einkaufsartikel ändern{p.name ? <>: <strong>{String(p.name)}</strong></> : null}</>;
      break;
  }
  return (
    <li>
      <span className="voice-op-icon" aria-hidden="true">{icon}</span>
      <span className="voice-op-text">
        {text}
        {persons.length > 0 && <PersonChips ids={persons} persons={me.persons} />}
        {issues.map((i, n) => <span key={n} className={`issue-${i.severity} small`}> {i.message}</span>)}
      </span>
    </li>
  );
}
